import type { ApiDebugStreamEvent, ApiStreamEvent } from "../api/run";
import type { AdminChangeReport, TagCropsPayload } from "../api/adminChat";
import type { MarvinQuestion } from "../hooks/streamReducer";
import type { ApiFile, ApiReference, ToolSuggestion } from "../shared/interfaces";
import { ApiStreamEventType } from "./streamProtocol";

export interface ContentBlock {
    content: string;
    isDone: boolean;
}

export interface StreamStep {
    code: string;
    message: string;
    callId?: string;
}

export interface ThinkingState {
    isThinking: boolean;
    buffer: string;
    durationMs: number | null;
}

export interface ToolCall {
    id: string;
    name: string;
    status: "running" | "completed" | "error";
}

interface ActiveToolCall {
    name: string;
    argsBuffer: string;
}

export interface RunStreamState {
    blocks: Map<string, ContentBlock>;
    isStreaming: boolean;
    isDone: boolean;
    status: "ok" | "cancelled" | "error" | null;
    error: string | null;
    steps: StreamStep[];
    referenceMap: Record<string, ApiReference>;
    files: ApiFile[];
    thinking: ThinkingState;
    toolCalls: ToolCall[];
    activeToolCalls: Map<string, ActiveToolCall>;
    runId: string | null;
    marvinMessageId: string | null;
    userQuery: string | null;
    toolSuggestions: ToolSuggestion[];
    adminChangeReports: AdminChangeReport[];
    tagCropBatches: TagCropsPayload[];
    questions: MarvinQuestion[];
    lastSeq: number;
}

const initialThinkingState: ThinkingState = {
    isThinking: false,
    buffer: "",
    durationMs: null,
};

export const initialStreamState: RunStreamState = {
    blocks: new Map(),
    isStreaming: false,
    isDone: false,
    status: null,
    error: null,
    steps: [],
    referenceMap: {},
    files: [],
    thinking: initialThinkingState,
    toolCalls: [],
    activeToolCalls: new Map(),
    runId: null,
    marvinMessageId: null,
    userQuery: null,
    toolSuggestions: [],
    adminChangeReports: [],
    tagCropBatches: [],
    questions: [],
    lastSeq: 0,
};

function getReferenceSectionLabel(reference: ApiReference): string | undefined {
    if (reference.sectionPath.length >= 2) {
        return reference.sectionPath.slice(0, 2).join(" - ");
    }
    return reference.sectionPath[0];
}

function getReferenceLabel(
    referenceMap: Record<string, ApiReference> | undefined,
    index: unknown,
): string | null {
    if (index === undefined || index === null) return null;
    const reference = referenceMap?.[String(index)];
    return reference ? (getReferenceSectionLabel(reference) ?? null) : null;
}

function truncateQuery(query: string, max = 50): string {
    return query.length > max ? query.slice(0, max) + "..." : query;
}

function getDestinationSuffix(args: Record<string, unknown>): string {
    return Array.isArray(args.destination_names) && args.destination_names.length > 0
        ? ` (${args.destination_names.join(", ")})`
        : "";
}

const DOC_TYPE_LABELS: Record<string, string> = {
    spec: "specs",
    drawing: "drawings",
    contract: "contracts",
    rfi: "RFIs",
    generic: "documents",
};

type ToolFormatter = (
    args: Record<string, unknown>,
    references?: Record<string, ApiReference>,
) => string;

const TOOL_FORMATTERS: Record<string, ToolFormatter> = {
    search_document: (args) => {
        const label = DOC_TYPE_LABELS[String(args.document_type || "")] || "documents";
        const query = String(args.query || "");
        return query ? `Searching ${label} for "${truncateQuery(query)}"` : `Searching ${label}...`;
    },
    search_schedule: (args) => {
        const query = String(args.query || "");
        if (query) return `Searching schedule for "${truncateQuery(query)}"`;
        const filters: string[] = [];
        if (args.critical_path_only) filters.push("critical path");
        if (args.behind_schedule) filters.push("behind schedule");
        if (args.milestones_only) filters.push("milestones");
        if (args.status) filters.push(String(args.status).replace("_", " "));
        if (args.project_name) filters.push(String(args.project_name));
        return filters.length > 0
            ? `Searching schedule (${filters.join(", ")})`
            : "Searching schedule...";
    },
    view_drawing_detail: (args, references) => {
        const label = getReferenceLabel(references, args.reference_index);
        return label
            ? `Reviewing drawing detail "${label}"`
            : `Reviewing drawing detail {${args.reference_index}}`;
    },
    find_references: (args, references) => {
        const label = getReferenceLabel(references, args.reference_index);
        return label
            ? `Finding references to "${label}"`
            : `Finding references to {${args.reference_index}}`;
    },
    view_tags: (args, references) => {
        const label = getReferenceLabel(references, args.reference_index);
        const suffix = getDestinationSuffix(args);
        return label
            ? `Inspecting tags on "${label}"${suffix}`
            : `Inspecting tags on {${args.reference_index}}${suffix}`;
    },
    search_tags: (args) => {
        const query = String(args.query || "");
        const suffix = getDestinationSuffix(args);
        return query ? `Searching tags for "${query}"${suffix}` : `Searching tags${suffix}...`;
    },
};

export function formatToolCallMessage(
    name: string,
    argsJson: string,
    referenceMap?: Record<string, ApiReference>,
): string | null {
    const formatter = TOOL_FORMATTERS[name];
    if (!formatter) return null;
    try {
        return formatter(JSON.parse(argsJson || "{}"), referenceMap);
    } catch {
        return formatter({}, referenceMap);
    }
}

function updateBlock(
    blocks: Map<string, ContentBlock>,
    blockId: string,
    update: Partial<ContentBlock> | ((block: ContentBlock) => ContentBlock),
): Map<string, ContentBlock> {
    const next = new Map(blocks);
    const existing = next.get(blockId);
    if (existing) {
        next.set(
            blockId,
            typeof update === "function" ? update(existing) : { ...existing, ...update },
        );
    }
    return next;
}

function addStep(
    steps: StreamStep[],
    code: string,
    message: string,
    callId?: string,
): StreamStep[] {
    return [...steps, { code, message, callId }];
}

function isAdminChangeReport(value: unknown): value is AdminChangeReport {
    if (typeof value !== "object" || value === null) return false;
    const candidate = value as { kind?: unknown; change_id?: unknown };
    return candidate.kind === "admin_change_report" && typeof candidate.change_id === "string";
}

function upsertAdminChangeReport(
    reports: AdminChangeReport[],
    report: AdminChangeReport,
): AdminChangeReport[] {
    const index = reports.findIndex((existing) => existing.change_id === report.change_id);
    if (index === -1) return [...reports, report];
    return reports.map((existing, current) => (current === index ? report : existing));
}

function isTagCropsPayload(value: unknown): value is TagCropsPayload {
    if (typeof value !== "object" || value === null) return false;
    const candidate = value as { kind?: unknown; crops?: unknown };
    return candidate.kind === "tag_crops" && Array.isArray(candidate.crops);
}

export function toApiStreamEvent(debugEvent: ApiDebugStreamEvent): ApiStreamEvent {
    return debugEvent.eventJson as unknown as ApiStreamEvent;
}

export function handleStreamEvent(state: RunStreamState, event: ApiStreamEvent): RunStreamState {
    if (event.seq > 0 && event.seq <= state.lastSeq) return state;
    const baseState = event.seq > 0 ? { ...state, lastSeq: event.seq } : state;

    switch (event.type) {
        case ApiStreamEventType.Step:
            return {
                ...baseState,
                steps: addStep(baseState.steps, event.data.code, event.data.message),
            };
        case ApiStreamEventType.Retrieval: {
            const { topDown, bottomUp, flatToc } = event.data;
            return {
                ...baseState,
                steps: addStep(
                    baseState.steps,
                    "retrieval_complete",
                    `Found ${topDown + bottomUp + flatToc} candidates`,
                ),
            };
        }
        case ApiStreamEventType.ReferencesStart:
        case ApiStreamEventType.ReferencesStop:
            return {
                ...baseState,
                referenceMap: event.data.references,
                files: event.data.files,
            };
        case ApiStreamEventType.LlmStart:
            return baseState;
        case ApiStreamEventType.ContentBlockStart: {
            const blocks = new Map(baseState.blocks);
            blocks.set(event.data.blockId, { content: "", isDone: false });
            return { ...baseState, blocks };
        }
        case ApiStreamEventType.ContentBlockDelta:
            if (!event.data.delta) return baseState;
            return {
                ...baseState,
                blocks: updateBlock(baseState.blocks, event.data.blockId, (block) => ({
                    ...block,
                    content: block.content + event.data.delta,
                })),
            };
        case ApiStreamEventType.ContentBlockStop:
            return {
                ...baseState,
                blocks: updateBlock(baseState.blocks, event.data.blockId, { isDone: true }),
            };
        case ApiStreamEventType.ThinkingStart:
            return {
                ...baseState,
                thinking: { isThinking: true, buffer: "", durationMs: null },
            };
        case ApiStreamEventType.ThinkingDelta:
            return {
                ...baseState,
                thinking: {
                    ...baseState.thinking,
                    buffer: baseState.thinking.buffer + event.data.delta,
                },
            };
        case ApiStreamEventType.ThinkingStop:
            return {
                ...baseState,
                thinking: {
                    ...baseState.thinking,
                    isThinking: false,
                    durationMs: event.data.durationMs,
                },
            };
        case ApiStreamEventType.LlmStop:
            return {
                ...baseState,
                steps: addStep(
                    baseState.steps,
                    "llm_complete",
                    `Completed ${event.data.scope || "content"}`,
                ),
            };
        case ApiStreamEventType.Error:
            return { ...baseState, error: event.data.error };
        case ApiStreamEventType.Done: {
            // The consumer owns closing UI blocks: a cancelled or failed run
            // sends no closing events, so DONE closes anything still open.
            const blocks = new Map(baseState.blocks);
            for (const [blockId, block] of blocks) {
                if (!block.isDone) blocks.set(blockId, { ...block, isDone: true });
            }
            return {
                ...baseState,
                blocks,
                thinking: baseState.thinking.isThinking
                    ? { ...baseState.thinking, isThinking: false }
                    : baseState.thinking,
                isStreaming: false,
                isDone: true,
                // `joined` (a waiting run's question read by the live run) is that run's answer.
                status: event.data.status === "joined" ? "ok" : event.data.status,
            };
        }
        case ApiStreamEventType.ToolCallStart: {
            const activeToolCalls = new Map(baseState.activeToolCalls);
            activeToolCalls.set(event.data.call_id, { name: event.data.name, argsBuffer: "" });
            return {
                ...baseState,
                activeToolCalls,
                toolCalls: [
                    ...baseState.toolCalls,
                    { id: event.data.call_id, name: event.data.name, status: "running" },
                ],
            };
        }
        case ApiStreamEventType.ToolCallDelta: {
            const active = baseState.activeToolCalls.get(event.data.call_id);
            if (!active) return baseState;
            const activeToolCalls = new Map(baseState.activeToolCalls);
            activeToolCalls.set(event.data.call_id, {
                ...active,
                argsBuffer: active.argsBuffer + (event.data.delta || ""),
            });
            return { ...baseState, activeToolCalls };
        }
        case ApiStreamEventType.ToolCallStop: {
            const active = baseState.activeToolCalls.get(event.data.call_id);
            if (!active) return baseState;
            const message = formatToolCallMessage(
                active.name,
                active.argsBuffer,
                baseState.referenceMap,
            );
            if (!message) return baseState;
            return {
                ...baseState,
                steps: addStep(baseState.steps, "tool_call", message, event.data.call_id),
            };
        }
        case ApiStreamEventType.ToolResult: {
            const toolCalls = baseState.toolCalls.map((toolCall) =>
                toolCall.id === event.data.call_id
                    ? {
                          ...toolCall,
                          status: event.data.error ? ("error" as const) : ("completed" as const),
                      }
                    : toolCall,
            );
            const structuredPayload = event.data.data;
            return {
                ...baseState,
                toolCalls,
                steps: baseState.steps.filter((step) => step.callId !== event.data.call_id),
                adminChangeReports: isAdminChangeReport(structuredPayload)
                    ? upsertAdminChangeReport(baseState.adminChangeReports, structuredPayload)
                    : baseState.adminChangeReports,
                tagCropBatches: isTagCropsPayload(structuredPayload)
                    ? [...baseState.tagCropBatches, structuredPayload]
                    : baseState.tagCropBatches,
            };
        }
        case ApiStreamEventType.ToolSuggestion:
            return { ...baseState, toolSuggestions: event.data.suggestions };
        case ApiStreamEventType.ReferencesDelta:
            return {
                ...baseState,
                referenceMap: { ...baseState.referenceMap, ...event.data.references },
                files: event.data.files
                    ? [...baseState.files, ...event.data.files]
                    : baseState.files,
            };
        case ApiStreamEventType.AskUser: {
            const question: MarvinQuestion = { ...event.data, status: "pending" };
            const exists = baseState.questions.some(
                (candidate) => candidate.questionId === question.questionId,
            );
            return {
                ...baseState,
                questions: exists
                    ? baseState.questions.map((candidate) =>
                          candidate.questionId === question.questionId ? question : candidate,
                      )
                    : [...baseState.questions, question],
            };
        }
        case ApiStreamEventType.AskUserAnswered:
            return {
                ...baseState,
                questions: baseState.questions.map((question) =>
                    question.questionId === event.data.questionId
                        ? {
                              ...question,
                              status: "answered" as const,
                              answers: event.data.answers,
                              otherText: event.data.otherText,
                          }
                        : question,
                ),
            };
        default:
            console.warn(`Unhandled stream event type: ${(event as { type: string }).type}`, event);
            return baseState;
    }
}

export function buildReplayState(events: ApiDebugStreamEvent[], upToIndex: number): RunStreamState {
    let state = initialStreamState;
    for (let index = 0; index < upToIndex && index < events.length; index += 1) {
        state = handleStreamEvent(state, toApiStreamEvent(events[index]));
    }
    return state;
}

export function getMarkdownContent(state: RunStreamState): string {
    return Array.from(state.blocks.values())
        .map((block) => block.content)
        .join("\n\n");
}
